import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AgendaClassificacao,
  AgendaClassificacaoRequest,
  AgendaContexto,
  AgendaConvenioRequest,
  AgendaEspecialidadeDaAgenda,
  AgendaEspecialidadeRequest,
  AgendaExame,
  AgendaGradeLinha,
  AgendaObservacaoRequest,
  AgendaOpcao,
  AgendaPlanoPaciente,
  AgendaProcedimentoRequest,
  AgendaStatusRequest,
  AgendaTipoAtendimentoOpcao,
  AgendaTipoAtendimentoRequest,
  AgendaTipoConsultaRequest,
} from '../../../../interfaces/agenda';
import { AgendaModaisService } from '../../../../services/agenda/agenda-modais.service';
import { AgendaService, mensagemErroAgenda } from '../../../../services/agenda/agenda.service';
import {
  CATEGORIA_PARTICULAR,
  CONVENIO_CARTAO,
  CONVENIO_PARTICULAR,
  OpcaoClassif,
  exibicaoConvenio,
  opcoesClassifModal,
} from '../agenda-agendamento/agenda-agendamento';
import {
  CampoLote,
  ContextoLote,
  EstadoLote,
  PassoLote,
  ROTULO_CAMPO,
  ResultadoItem,
  ResultadoLinha,
  ResumoResultado,
  STATUS_LOTE,
  TravaLinha,
  estadoLoteInicial,
  faltandoLote,
  linhasQueApagamObservacao,
  motivoCampoSelecao,
  motivoEspecialidadeDaAgenda,
  motivoLinhaLote,
  passosDaLinha,
  planoDaLinha224,
  resumirResultados,
  tipoSelecao,
  travasLinha,
} from './agenda-alterar';

const ESPERA_BUSCA_EXAME_MS = 500;
const MINIMO_BUSCA = 3;
/** Falha nestes campos encerra a linha: é regra de negócio, não "tenta o próximo" (original). */
const CAMPOS_QUE_ENCERRAM: CampoLote[] = ['especialidade', 'procedimento'];

/**
 * Alteração em lote dos agendamentos selecionados: só os campos preenchidos são aplicados, campo
 * a campo e linha a linha, cada um na sua rota; o resultado fica na tela, por linha.
 */
@Component({
  selector: 'app-agenda-alterar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agenda-alterar.component.html',
  styleUrl: './agenda-alterar.component.scss',
})
export class AgendaAlterarComponent implements OnInit, OnDestroy {
  @Input({ required: true }) linhas!: AgendaGradeLinha[];
  @Input() contexto: AgendaContexto | null = null;
  @Input() classificacoes: AgendaClassificacao[] = [];
  @Input() cdEstabelecimento: number | null = null;
  /** Trocar para outra especialidade tira a linha da grade filtrada: o resultado avisa. */
  @Input() cdEspecialidadeFiltro: number | null = null;

  @Output() fechar = new EventEmitter<void>();
  /** Terminou de aplicar (com ou sem erro): a página recarrega a grade. */
  @Output() concluido = new EventEmitter<void>();

  readonly rotuloCampo = ROTULO_CAMPO;
  readonly CONVENIO_CARTAO = CONVENIO_CARTAO;
  readonly exibicaoConvenio = exibicaoConvenio;

  e: EstadoLote = estadoLoteInicial();

  convenios: AgendaOpcao[] = [];
  conveniosCarregados = false;
  carregandoConvenios = false;
  categorias: AgendaOpcao[] = [];
  carregandoCategorias = false;
  planos: Record<string, AgendaPlanoPaciente | null> = {};
  carregandoPlanos = false;

  especialidades: AgendaEspecialidadeDaAgenda[] = [];
  carregandoEspecialidades = false;
  erroEspecialidades = '';

  buscaExame = '';
  exames: AgendaExame[] = [];
  buscandoExame = false;
  vazioExame = '';
  exameEscolhido: { nrSeqProcInterno: number; dsProcExame: string } | null = null;

  /** Observação vazia em lote: o primeiro Aplicar só pede confirmação. */
  pedindoConfirmacao = false;
  confirmouApagar = false;

  confirmarApagar() {
    this.confirmouApagar = true;
    void this.aplicar();
  }

  tiposAtendimento: AgendaTipoAtendimentoOpcao[] = [];
  carregandoTipos = false;

  salvando = false;
  /** null = ainda no formulário; preenchido linha a linha durante a aplicação. */
  resultados: ResultadoLinha[] | null = null;

  private fecharRegistro: (() => void) | null = null;
  private timerExame: ReturnType<typeof setTimeout> | null = null;
  private seq = { categorias: 0, planos: 0, exame: 0 };

  constructor(private api: AgendaService, private modais: AgendaModaisService) {}

  ngOnInit() {
    this.fecharRegistro = this.modais.abrir();
    // Com um só, a observação começa com a atual; "só envia se mudou" compara com ela.
    if (this.linhas.length === 1) this.e.observacao = this.linhas[0].dsObservacao ?? '';
    void this.carregarConvenios();
    if (!motivoCampoSelecao('tipoAtendimento', this.linhas)) void this.carregarTiposAtendimento();
    if (!motivoCampoSelecao('especialidade', this.linhas)) void this.carregarEspecialidades(this.linhas[0].cdAgenda);
  }

  ngOnDestroy() {
    if (this.timerExame) clearTimeout(this.timerExame);
    this.seq.exame++;
    this.seq.categorias++;
    this.seq.planos++;
    this.fecharRegistro?.();
    this.fecharRegistro = null;
  }

  @HostListener('document:keydown.escape')
  aoEsc() {
    if (!this.modais.coberto()) this.cancelar();
  }

  cancelar() {
    if (!this.salvando) this.fechar.emit();
  }

  get supervisor(): boolean { return !!this.contexto?.supervisor; }
  get tipo() { return tipoSelecao(this.linhas); }
  get titulo(): string { return this.linhas.length > 1 ? 'Alteração em lote' : 'Alterar agendamento'; }
  get temReservadoBloqueado(): boolean { return this.linhas.some(l => !!motivoLinhaLote(l, this.supervisor)); }

  readonly opcoesStatus = STATUS_LOTE;

  /** "Encaixe" (exame) para todos: no original o Alterar oferecia, e a rota não confere encaixe. */
  get opcoesClassif(): OpcaoClassif[] {
    const t = this.tipo;
    if (t !== 'CONSULTA' && t !== 'EXAME') return [];
    return opcoesClassifModal(t, this.classificacoes, true, {}, this.supervisor);
  }

  get apagaObservacao(): number { return linhasQueApagamObservacao(this.linhas, this.e, this.ctxLote); }

  /** Qualquer mudança na observação desfaz a confirmação de apagar. */
  aoMudarObservacao() {
    this.confirmouApagar = false;
    this.pedindoConfirmacao = false;
  }

  get retornoEscolhido(): boolean { return this.e.classif === 'R' || this.e.classif === 'R13'; }

  travas(l: AgendaGradeLinha): TravaLinha[] { return travasLinha(l, this.supervisor); }

  trava(campo: CampoLote): string | null {
    const m = motivoCampoSelecao(campo, this.linhas);
    if (m || campo !== 'especialidade') return m;
    if (this.carregandoEspecialidades) return null;
    if (this.erroEspecialidades) return this.erroEspecialidades;
    return motivoEspecialidadeDaAgenda(this.especialidades, this.linhas);
  }

  get ctxLote(): ContextoLote { return { supervisor: this.supervisor, planos: this.planos }; }

  get faltando(): string[] { return faltandoLote(this.e, this.carregandoPlanos); }

  /** Pacientes que o 224 vai pular (sem plano ou plano não lido). */
  get semPlano224(): number {
    if (this.e.cdConvenio !== CONVENIO_CARTAO || this.carregandoPlanos) return 0;
    return this.linhas.filter(l => !motivoLinhaLote(l, this.supervisor) && planoDaLinha224(l, this.planos).motivo).length;
  }

  get resumo(): ResumoResultado { return resumirResultados(this.resultados ?? []); }

  // --- Convênio e categoria -------------------------------------------------------------------

  /** Lote com agendas diferentes não tem um médico só para a regra: vai sem cdAgenda. */
  get cdAgendaUnica(): number | null {
    const cd = this.linhas[0]?.cdAgenda ?? null;
    return cd && this.linhas.every(l => l.cdAgenda === cd) ? cd : null;
  }

  private async carregarConvenios() {
    if (!this.cdEstabelecimento) return;
    this.carregandoConvenios = true;
    try {
      this.convenios = await this.api.convenios(this.cdEstabelecimento, this.cdAgendaUnica);
      this.conveniosCarregados = true;
    } catch {
      this.convenios = [];
    } finally {
      this.carregandoConvenios = false;
    }
  }

  async trocarConvenio(cd: number | null) {
    this.e = { ...this.e, cdConvenio: cd, cdCategoria: null };
    this.categorias = [];
    if (cd === CONVENIO_CARTAO) {
      await this.carregarPlanos();
    } else if (cd != null) {
      await this.carregarCategorias(cd);
    }
  }

  private async carregarCategorias(cd: number) {
    const seq = ++this.seq.categorias;
    this.carregandoCategorias = true;
    try {
      const lista = await this.api.categorias(cd, this.cdAgendaUnica);
      if (seq !== this.seq.categorias) return;
      this.categorias = lista;
      const particular = cd === CONVENIO_PARTICULAR && lista.some(c => String(c.codigo) === CATEGORIA_PARTICULAR);
      const unica = lista.length === 1 ? String(lista[0].codigo) : null;
      this.e = { ...this.e, cdCategoria: particular ? CATEGORIA_PARTICULAR : unica };
    } catch {
      if (seq === this.seq.categorias) this.categorias = [];
    } finally {
      if (seq === this.seq.categorias) this.carregandoCategorias = false;
    }
  }

  /** No 224 a categoria é a do plano de cada paciente: o mesmo plano que a gravação confere. */
  private async carregarPlanos() {
    const seq = ++this.seq.planos;
    const cds = [...new Set(this.linhas
      .filter(l => !motivoLinhaLote(l, this.supervisor))
      .map(l => (l.paciente?.cdPessoaFisica ?? '').trim())
      .filter(cd => cd && !(cd in this.planos)))];
    if (!cds.length) return;
    this.carregandoPlanos = true;
    try {
      const lidos = await Promise.all(cds.map(cd => this.api.planoPaciente(cd).catch(() => null)));
      if (seq !== this.seq.planos) return;
      const planos = { ...this.planos };
      cds.forEach((cd, i) => { planos[cd] = lidos[i]; });
      this.planos = planos;
    } finally {
      if (seq === this.seq.planos) this.carregandoPlanos = false;
    }
  }

  private async carregarTiposAtendimento() {
    this.carregandoTipos = true;
    try {
      this.tiposAtendimento = await this.api.tiposAtendimento();
    } catch {
      this.tiposAtendimento = [];
    } finally {
      this.carregandoTipos = false;
    }
  }

  // --- Especialidade e procedimento -----------------------------------------------------------

  private async carregarEspecialidades(cdAgenda: number) {
    this.carregandoEspecialidades = true;
    try {
      this.especialidades = await this.api.especialidadesDaAgenda(cdAgenda);
    } catch (e) {
      this.especialidades = [];
      this.erroEspecialidades = mensagemErroAgenda(e, 'Não foi possível carregar as especialidades da agenda.');
    } finally {
      this.carregandoEspecialidades = false;
    }
  }

  get especialidadeAtual(): string {
    return this.linhas.length === 1 ? this.linhas[0].especialidade?.dsEspecialidade || '—' : '';
  }

  aoDigitarExame() {
    if (this.timerExame) clearTimeout(this.timerExame);
    const q = this.buscaExame.trim();
    const seq = ++this.seq.exame;
    if (q.length < MINIMO_BUSCA) {
      this.exames = [];
      this.vazioExame = '';
      this.buscandoExame = false;
      return;
    }
    const cdAgenda = this.linhas[0].cdAgenda;
    this.timerExame = setTimeout(async () => {
      this.buscandoExame = true;
      try {
        const lista = await this.api.exames(q, cdAgenda);
        if (seq !== this.seq.exame) return;
        // No ramo livre (agenda sem regra) as linhas não têm agenda: filtrar zeraria a lista.
        const livre = lista[0]?.ieProcLivre === 'S';
        const daAgenda = livre ? lista : lista.filter(x => x.cdAgenda === cdAgenda);
        const vistos = new Set<number>();
        this.exames = daAgenda.filter(x => !vistos.has(x.nrSeqProcInterno) && !!vistos.add(x.nrSeqProcInterno));
        this.vazioExame = this.exames.length ? '' : `Nenhum exame encontrado${lista.length ? ' nesta agenda' : ''}.`;
      } catch {
        if (seq === this.seq.exame) { this.exames = []; this.vazioExame = 'Erro ao buscar exames.'; }
      } finally {
        if (seq === this.seq.exame) this.buscandoExame = false;
      }
    }, ESPERA_BUSCA_EXAME_MS);
  }

  escolherExame(x: AgendaExame) {
    this.exameEscolhido = { nrSeqProcInterno: x.nrSeqProcInterno, dsProcExame: x.dsProcExame ?? '' };
    this.e = { ...this.e, nrSeqProcInterno: x.nrSeqProcInterno };
    this.buscaExame = '';
    this.exames = [];
    this.vazioExame = '';
  }

  removerExame() {
    this.exameEscolhido = null;
    this.e = { ...this.e, nrSeqProcInterno: null };
  }

  // --- Aplicar --------------------------------------------------------------------------------

  async aplicar() {
    if (this.salvando || this.resultados || this.faltando.length) return;
    if (this.apagaObservacao && !this.confirmouApagar) {
      this.pedindoConfirmacao = true;
      return;
    }
    this.salvando = true;
    this.resultados = [];
    try {
      for (const l of this.linhas) {
        const r: ResultadoLinha = { linha: l, itens: [] };
        this.resultados.push(r);
        let encerrada: string | null = null;
        for (const p of passosDaLinha(l, this.e, this.ctxLote)) {
          const rotulo = ROTULO_CAMPO[p.campo];
          if (p.motivo) {
            r.itens.push({ campo: p.campo, rotulo, situacao: p.igual ? 'igual' : 'pulado', mensagem: p.motivo });
            continue;
          }
          if (encerrada) {
            r.itens.push({ campo: p.campo, rotulo, situacao: 'pulado', mensagem: `Não aplicado: ${encerrada}` });
            continue;
          }
          const item = await this.executar(l, p);
          r.itens.push(item);
          if (item.situacao === 'erro' && CAMPOS_QUE_ENCERRAM.includes(p.campo)) encerrada = `a troca de ${rotulo.toLowerCase()} falhou`;
        }
      }
    } finally {
      this.salvando = false;
      this.concluido.emit();
    }
  }

  private async executar(l: AgendaGradeLinha, p: PassoLote): Promise<ResultadoItem> {
    const base = { campo: p.campo, rotulo: ROTULO_CAMPO[p.campo] };
    const id = l.nrSequencia;
    try {
      let mensagem = 'Alterado';
      switch (p.campo) {
        case 'status': {
          const r = await this.api.alterarStatus(id, p.corpo as AgendaStatusRequest);
          if (r.dado?.hrAjustada) mensagem = `Horário ajustado para ${r.dado.hrAjustada} — outro paciente já ocupava este minuto com o mesmo status.`;
          break;
        }
        case 'classificacao':
          await this.api.alterarClassificacao(id, p.corpo as AgendaClassificacaoRequest);
          break;
        case 'tipoAtendimento':
          await this.api.alterarTipoAtendimento(id, p.corpo as AgendaTipoAtendimentoRequest);
          break;
        case 'tipoConsulta':
          await this.api.alterarTipoConsulta(id, p.corpo as AgendaTipoConsultaRequest);
          break;
        case 'convenio':
          await this.api.alterarConvenio(id, p.corpo as AgendaConvenioRequest);
          break;
        case 'observacao':
          await this.api.alterarObservacao(id, p.corpo as AgendaObservacaoRequest);
          break;
        case 'procedimento': {
          const r = await this.api.alterarProcedimento(id, p.corpo as AgendaProcedimentoRequest);
          mensagem = `Agora ${r.dado?.dsProcExame || 'o exame escolhido'}`;
          break;
        }
        case 'especialidade': {
          const corpo = p.corpo as AgendaEspecialidadeRequest;
          const r = await this.api.alterarEspecialidade(id, corpo);
          mensagem = `Agora ${r.dado?.dsEspecialidade || 'a escolhida'}`;
          if (this.cdEspecialidadeFiltro && this.cdEspecialidadeFiltro !== corpo.cdEspecialidade) {
            mensagem += '. Sai desta lista porque o filtro mostra outra especialidade';
          }
          break;
        }
      }
      return { ...base, situacao: 'ok', mensagem };
    } catch (e) {
      return { ...base, situacao: 'erro', mensagem: mensagemErroAgenda(e, `Erro ao alterar ${base.rotulo.toLowerCase()}.`) };
    }
  }
}
